import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Warning, ErrorOutline, Info, CheckCircle } from '@mui/icons-material';

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  variant?: 'warning' | 'error' | 'info' | 'success';
  loading?: boolean;
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'warning',
  loading = false
}) => {
  const theme = useTheme();

  const getIconAndColor = () => {
    switch (variant) {
      case 'error':
        return {
          icon: <ErrorOutline sx={{ fontSize: 64 }} />,
          color: theme.palette.error.main,
          bgColor: alpha(theme.palette.error.main, 0.08)
        };
      case 'warning':
        return {
          icon: <Warning sx={{ fontSize: 64 }} />,
          color: theme.palette.warning.main,
          bgColor: alpha(theme.palette.warning.main, 0.08)
        };
      case 'info':
        return {
          icon: <Info sx={{ fontSize: 64 }} />,
          color: theme.palette.info.main,
          bgColor: alpha(theme.palette.info.main, 0.08)
        };
      case 'success':
        return {
          icon: <CheckCircle sx={{ fontSize: 64 }} />,
          color: theme.palette.success.main,
          bgColor: alpha(theme.palette.success.main, 0.08)
        };
      default:
        return {
          icon: <Warning sx={{ fontSize: 64 }} />,
          color: theme.palette.warning.main,
          bgColor: alpha(theme.palette.warning.main, 0.08)
        };
    }
  };

  const { icon, color, bgColor } = getIconAndColor();

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: theme.customShadows?.z24 || theme.shadows[24]
        }
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: 2
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: bgColor,
              color: color,
              mb: 2
            }}
          >
            {icon}
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center' }}>
            {title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <DialogContentText
          sx={{
            textAlign: 'center',
            color: theme.palette.text.secondary,
            fontSize: '1rem'
          }}
        >
          {message}
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3 }}>
        <Button
          onClick={onCancel}
          variant="outlined"
          color="inherit"
          size="large"
          disabled={loading}
          sx={{
            minWidth: 120,
            borderRadius: 1
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={variant}
          size="large"
          disabled={loading}
          sx={{
            minWidth: 120,
            borderRadius: 1
          }}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}
        >
          {loading ? 'Deleting...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
